"use client";

import { useEffect, useMemo, useState } from "react";
import { FileTextIcon } from "lucide-react";

import {
  AVS_SELLING_TYPE_VDL,
  AVS_SELLING_TYPE_VDS,
} from "@/components/backoffice/cat/vehicle-assignment/sales-channels-step";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from "@/components/ui/combobox";
import { Skeleton } from "@/components/ui/skeleton";
import { useShippingSitesQuery } from "@/lib/queries";

type FranchiseChoice = "yes" | "no" | "";

export interface AvsVdlVdsAdditionalInformationsProps {
  salesChannel: typeof AVS_SELLING_TYPE_VDL | typeof AVS_SELLING_TYPE_VDS;
  onCanProceedChange?: (canProceed: boolean) => void;
}

export function AvsVdlVdsAdditionalInformations({
  salesChannel,
  onCanProceedChange,
}: AvsVdlVdsAdditionalInformationsProps) {
  const isVdl = salesChannel === AVS_SELLING_TYPE_VDL;
  const isVds = salesChannel === AVS_SELLING_TYPE_VDS;

  const [intranetOrderNumber, setIntranetOrderNumber] = useState("");
  const [contractReferenceNumber, setContractReferenceNumber] = useState("");
  const [franchiseCode, setFranchiseCode] = useState<FranchiseChoice>("");
  const [driverName, setDriverName] = useState("");
  const [preferredDeliveryDate, setPreferredDeliveryDate] = useState("");
  const [deliveryLocation, setDeliveryLocation] = useState<{
    value: string;
    label: string;
  } | null>(null);

  const shippingSitesQuery = useShippingSitesQuery({ sort: { name: "asc" } });

  const deliveryLocationOptions = useMemo(() => {
    const sites = shippingSitesQuery.data?.shippingSites ?? [];
    return sites
      .filter((site) => site.isActive)
      .map((site) => ({ value: String(site.id), label: site.name }));
  }, [shippingSitesQuery.data?.shippingSites]);

  const autoDriverName = "—";

  const canProceed = useMemo(() => {
    if (!intranetOrderNumber.trim() || !preferredDeliveryDate) {
      return false;
    }
    if (isVds && !contractReferenceNumber.trim()) {
      return false;
    }
    if (isVdl && franchiseCode === "") {
      return false;
    }
    if (isVdl && !driverName.trim()) {
      return false;
    }
    if (!deliveryLocation) {
      return false;
    }
    return true;
  }, [
    contractReferenceNumber,
    deliveryLocation,
    driverName,
    franchiseCode,
    intranetOrderNumber,
    isVdl,
    isVds,
    preferredDeliveryDate,
  ]);

  useEffect(() => {
    onCanProceedChange?.(canProceed);
  }, [canProceed, onCanProceedChange]);

  return (
    <Card>
      <CardHeader className="space-y-3 pb-2">
        <CardTitle className="flex items-center gap-2 text-base font-semibold">
          <FileTextIcon className="text-primary h-5 w-5 shrink-0" aria-hidden />
          Informations complémentaires
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6 pt-2">
        <FieldGroup className="grid gap-4 md:grid-cols-2">
          <Field>
            <FieldLabel htmlFor="vdl-vds-intranet-order">
              N° de commande intranet{" "}
              <span className="text-destructive">*</span>
            </FieldLabel>
            <Input
              id="vdl-vds-intranet-order"
              value={intranetOrderNumber}
              onChange={(event) => setIntranetOrderNumber(event.target.value)}
              placeholder="S223947474"
            />
          </Field>

          <Field>
            <FieldLabel htmlFor="vdl-vds-contract-ref">
              N° de contrat réf
              {isVds ? <span className="text-destructive"> *</span> : null}
            </FieldLabel>
            <Input
              id="vdl-vds-contract-ref"
              value={contractReferenceNumber}
              onChange={(event) =>
                setContractReferenceNumber(event.target.value)
              }
              placeholder="209342"
            />
          </Field>

          {isVdl ? (
            <Field className="md:col-span-2">
              <FieldLabel>
                Code franchise <span className="text-destructive">*</span>
              </FieldLabel>
              <RadioGroup
                value={franchiseCode || undefined}
                onValueChange={(value) =>
                  setFranchiseCode(value as FranchiseChoice)
                }
                className="flex gap-6"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="yes" id="franchise-yes" />
                  <Label htmlFor="franchise-yes">Oui</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="no" id="franchise-no" />
                  <Label htmlFor="franchise-no">Non</Label>
                </div>
              </RadioGroup>
            </Field>
          ) : null}

          <Field>
            <FieldLabel htmlFor="vdl-vds-driver">
              Conducteur
              {isVdl ? <span className="text-destructive"> *</span> : null}
            </FieldLabel>
            {isVdl ? (
              <Input
                id="vdl-vds-driver"
                value={driverName}
                onChange={(event) => setDriverName(event.target.value)}
                placeholder="Edouard LEFORT"
              />
            ) : (
              <>
                <Input
                  id="vdl-vds-driver"
                  value={autoDriverName}
                  readOnly
                  disabled
                  aria-readonly
                />
                <FieldDescription>
                  Rempli automatiquement à partir des données client.
                </FieldDescription>
              </>
            )}
          </Field>

          <Field>
            <FieldLabel htmlFor="vdl-vds-preferred-delivery-date">
              Date de livraison souhaitée{" "}
              <span className="text-destructive">*</span>
            </FieldLabel>
            <DatePicker
              id="vdl-vds-preferred-delivery-date"
              value={
                preferredDeliveryDate
                  ? new Date(preferredDeliveryDate)
                  : undefined
              }
              onChange={(date) =>
                setPreferredDeliveryDate(date?.toString() ?? "")
              }
              min={new Date()}
            />
          </Field>

          <Field>
            <FieldLabel htmlFor="vdl-vds-delivery-location">
              Lieu de livraison <span className="text-destructive">*</span>
            </FieldLabel>
            {shippingSitesQuery.isPending ? (
              <Skeleton className="h-9 w-full max-w-md rounded-md" />
            ) : shippingSitesQuery.isError ? (
              <p className="text-destructive text-sm" role="alert">
                Impossible de charger les lieux de livraison.
              </p>
            ) : deliveryLocationOptions.length === 0 ? (
              <p className="text-destructive text-sm" role="alert">
                Aucun lieu de livraison actif disponible.
              </p>
            ) : (
              <Combobox
                items={deliveryLocationOptions}
                itemToStringLabel={(item) => item.label}
                value={deliveryLocation}
                onValueChange={(next) => setDeliveryLocation(next)}
              >
                <ComboboxInput
                  id="vdl-vds-delivery-location"
                  placeholder="Sélectionner un lieu"
                  showClear={deliveryLocation !== null}
                  aria-label="Lieu de livraison"
                  className="max-w-md"
                />
                <ComboboxContent>
                  <ComboboxEmpty>Aucun lieu trouvé.</ComboboxEmpty>
                  <ComboboxList>
                    {(item) => (
                      <ComboboxItem key={item.value} value={item}>
                        {item.label}
                      </ComboboxItem>
                    )}
                  </ComboboxList>
                </ComboboxContent>
              </Combobox>
            )}
          </Field>
        </FieldGroup>
      </CardContent>
    </Card>
  );
}
